import { getIdToken } from './firebase';

function getApiUrl(): string {
  const env = typeof import.meta !== 'undefined' ? (import.meta as any).env ?? {} : {};
  return env.VITE_API_URL || (typeof process !== 'undefined' && process.env.VITE_API_URL) || '';
}

export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
    public detail?: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

// Authenticated fetch with automatic token refresh on 401.
export async function authenticatedFetch(
  path: string,
  options: RequestInit = {},
): Promise<Response> {
  const baseUrl = getApiUrl().replace(/\/+$/, '');
  const url = `${baseUrl}${path}`;

  const token = await getIdToken();
  const headers = new Headers(options.headers);
  if (token) {
    headers.set('Authorization', `Bearer ${token}`);
  }
  // Only when there is a body: Fastify answers 400 to an empty body sent as application/json.
  if (options.body != null) {
    headers.set('Content-Type', 'application/json');
  }

  let response = await fetch(url, { ...options, headers });

  // 401 = token expired; refresh once and retry.
  if (response.status === 401 && token) {
    const freshToken = await getIdToken(true);
    if (freshToken && freshToken !== token) {
      headers.set('Authorization', `Bearer ${freshToken}`);
      response = await fetch(url, { ...options, headers });
    }
  }

  return response;
}

export async function apiFetch<T>(
  path: string,
  options: RequestInit = {},
): Promise<T> {
  const response = await authenticatedFetch(path, options);

  if (!response.ok) {
    let body: any;
    try {
      body = await response.json();
    } catch {
      body = {};
    }
    throw new ApiError(
      response.status,
      body.code || `HTTP_${response.status}`,
      body.error || response.statusText,
      body.detail,
    );
  }

  // 204 No Content
  if (response.status === 204) {
    return undefined as unknown as T;
  }

  return response.json();
}
